import React, { useState, useEffect } from 'react';
import { useOrder } from '../../context/OrderContext';
import { RALModal } from '../../modals';

// Helper to check if finish requires RAL code
const isSpecialFinish = (code) => code === 'V1' || code === 'V2';

// Cap configuration rules for each profile type
const CAP_CONFIGS = {
    '6': {
        codes: ['N6-6*', 'N6-7*DX', 'N6-7*SX'],
        hasSides: true,
        hasMultipleTypes: true,
        description: 'Valido solo per NINFA 6',
        image: '/images/accessories/TAPPI/SEL-N6-6.png'
    },
    '6S': {
        codes: ['N6S-6*'],
        hasSides: false,
        description: 'Valido solo per NINFA 6 STADIO',
        image: '/images/accessories/TAPPI/SEL-N6S-6.png'
    },
    '106': {
        codes: ['N106-6*'],
        hasSides: false,
        description: 'Valido solo per NINFA 106',
        image: '/images/accessories/TAPPI/SEL-N106-6.png'
    },
    '116': {
        codes: ['N116-6*DX', 'N116-6*SX'],
        hasSides: true,
        description: 'Valido solo per NINFA 116',
        imageDX: '/images/accessories/TAPPI/SEL-N116-6DX.png',
        imageSX: '/images/accessories/TAPPI/SEL-N116-6SX.png'
    },
    '56': {
        codes: ['N56-6*', 'N56-7*DX', 'N56-7*SX'],
        hasSides: true,
        description: 'Valido solo per NINFA 56',
        hasMultipleTypes: true,
        image7DX: '/images/accessories/TAPPI/SEL-N56-7DX.png',
        image6SX: '/images/accessories/TAPPI/SEL-56-6SX.png'
    },
    '50': {
        codes: ['N50-6*DX', 'N50-6*SX'],
        hasSides: true,
        description: 'Valido solo per NINFA 50',
        imageDX: '/images/accessories/TAPPI/SEL-N50-6DX.png',
        imageSX: '/images/accessories/TAPPI/SEL-N50-6SX.png',
        image: '/images/accessories/TAPPI/SEL-50-6.png'
    },
    '176': {
        codes: ['N166-6*'],
        hasSides: false,
        description: 'Valido per NINFA 176',
        image: '/images/accessories/TAPPI/SEL-N166-6.png'
    },
    '186': {
        codes: ['N166-6*'],
        hasSides: false,
        description: 'Valido per NINFA 186',
        image: '/images/accessories/TAPPI/SEL-N166-6.png'
    }
};

// Available finishes
const FINISHES = [
    { code: 'A', name: 'Argento', color: '#C0C0C0' },
    { code: 'B', name: 'RAL9010 Opaco', color: '#FFFFFF' },
    { code: 'N', name: 'RAL9005 Opaco', color: '#000000' },
    { code: 'C', name: 'Grigio Antracite - RAL 7016 Opaco', color: '#293133' },
    { code: 'G', name: 'Grezzi', color: '#E0E0E0' },
    { code: 'V1', name: 'Verniciato Speciale 1', color: '#D3D3D3' },
    { code: 'V2', name: 'Verniciato Speciale 2', color: '#B8B8B8' }
];

export const CapSelector = ({ onSelect, initialValues = {}, profileType: propProfileType }) => {
    const { state } = useOrder();
    // Use profileType from props (passed from navigation) or fall back to order state
    const profileType = propProfileType || state?.orderDetails?.profileType || state?.orderDetails?.ninfaNumber;

    // Normalize profile type to extract number only (e.g., "N6" -> "6", "N106" -> "106")
    // Note: '6S' stays as '6S' because it has its own cap config (N6S-6*)
    const normalizedProfileType = profileType ? profileType.toString().replace(/^N/, '') : null;

    // State management
    const [step, setStep] = useState(1);
    const [selectedCaps, setSelectedCaps] = useState(initialValues?.selectedCaps || []);
    const [selectedProfileForAccessory, setSelectedProfileForAccessory] = useState(normalizedProfileType || '');

    // RAL Modal state
    const [showRALModal, setShowRALModal] = useState(false);
    const [pendingCapType, setPendingCapType] = useState(null);
    const [pendingFinishCode, setPendingFinishCode] = useState(null);

    // Get available caps - filtered by profile or all if accessory-only flow
    const getAvailableCaps = () => {
        const allCaps = [];

        const getImageForCap = (code, config) => {
            if (code.includes('7') && code.includes('DX')) {
                return config.image7DX;
            }
            if (code.includes('6') && code.includes('SX')) {
                return config.image6SX || config.imageSX;
            }
            if (code.includes('DX')) {
                return config.imageDX || config.image;
            }
            if (code.includes('SX')) {
                return config.imageSX || config.image;
            }
            return config.image;
        };

        if (normalizedProfileType && CAP_CONFIGS[normalizedProfileType]) {
            // If we have a specific profile, only show caps for that profile
            const config = CAP_CONFIGS[normalizedProfileType];
            config.codes.forEach(code => {
                allCaps.push({
                    code,
                    profile: normalizedProfileType,
                    name: code.includes('DX') ? 'Tappo Destro' :
                        code.includes('SX') ? 'Tappo Sinistro' : 'Tappo Standard',
                    description: config.description,
                    image: getImageForCap(code, config)
                });
            });
        } else {
            // If no profile selected (standalone accessories order), show all caps
            Object.entries(CAP_CONFIGS).forEach(([profile, config]) => {
                config.codes.forEach(code => {
                    allCaps.push({
                        code,
                        profile,
                        name: code.includes('DX') ? 'Tappo Destro' :
                            code.includes('SX') ? 'Tappo Sinistro' : 'Tappo Standard',
                        description: config.description,
                        image: getImageForCap(code, config)
                    });
                });
            });
        }
        return allCaps;
    };

    // Get profile configuration
    const profileConfig = CAP_CONFIGS[normalizedProfileType || selectedProfileForAccessory];
    const isAccessoryOnlyFlow = !normalizedProfileType; // True when ordering accessories without selecting profile first

    // Reset when profile changes
    useEffect(() => {
        if (profileType && profileType !== selectedProfileForAccessory) {
            setSelectedProfileForAccessory(profileType);
            setStep(1);
            setSelectedCaps([]);
        }
    }, [profileType, selectedProfileForAccessory]);

    const handleCapSelection = (capData) => {
        // capData can be either a string (profile-specific flow) or object (accessory-only flow)
        const capType = typeof capData === 'string' ? capData : capData.code;
        const capProfile = typeof capData === 'string' ? (profileType || selectedProfileForAccessory) : capData.profile;

        setSelectedCaps(prev => {
            const existing = prev.find(cap => cap.type === capType);
            if (existing) {
                return prev.filter(cap => cap.type !== capType);
            } else {
                return [...prev, { type: capType, quantity: 1, profile: capProfile, finish: 'A' }]; // Default to Argento
            }
        });
    };

    const updateCapQuantity = (capType, quantity) => {
        setSelectedCaps(prev =>
            prev.map(cap =>
                cap.type === capType ? { ...cap, quantity: quantity === '' ? '' : Math.max(1, quantity) } : cap
            )
        );
    };

    const updateCapFinish = (capType, finishCode) => {
        if (isSpecialFinish(finishCode)) {
            // Show RAL modal for V1/V2
            setPendingCapType(capType);
            setPendingFinishCode(finishCode);
            setShowRALModal(true);
        } else {
            // Normal finish selection
            setSelectedCaps(prev =>
                prev.map(cap =>
                    cap.type === capType ? { ...cap, finish: finishCode, ralCode: null } : cap
                )
            );
        }
    };

    // Handle RAL confirmation
    const handleRALConfirm = (ralCode) => {
        setShowRALModal(false);
        setSelectedCaps(prev =>
            prev.map(cap =>
                cap.type === pendingCapType ? { ...cap, finish: pendingFinishCode, ralCode } : cap
            )
        );
        setPendingCapType(null);
        setPendingFinishCode(null);
    };

    // Handle RAL modal close
    const handleRALClose = () => {
        setShowRALModal(false);
        setPendingCapType(null);
        setPendingFinishCode(null);
    };

    const generateProductCodes = () => {
        return selectedCaps.map(cap => {
            const baseCode = cap.type.replace('*', cap.finish);
            const profileInfo = isAccessoryOnlyFlow ? ` (NINFA ${cap.profile})` : '';
            const finishName = FINISHES.find(f => f.code === cap.finish)?.name || cap.finish;
            const ralInfo = cap.ralCode ? ` - ${cap.ralCode}` : '';
            return {
                code: baseCode,
                quantity: cap.quantity,
                profile: cap.profile,
                ralCode: cap.ralCode || null,
                description: `Tappo ${cap.type.includes('DX') ? 'Destro' : cap.type.includes('SX') ? 'Sinistro' : 'Standard'} - ${finishName}${ralInfo}${profileInfo}`
            };
        });
    };

    const handleSubmit = () => {
        const productCodes = generateProductCodes();
        // Get ralCode from any cap that has one (they all should have the same if V1/V2 was selected)
        const anyRalCode = selectedCaps.find(cap => cap.ralCode)?.ralCode || null;
        const capConfig = {
            selectedCaps,
            productCodes,
            profileType,
            ralCode: anyRalCode,
            finish: selectedCaps[0]?.finish || null
        };

        onSelect(capConfig);
    };

    // Get available caps based on context
    const availableCaps = getAvailableCaps();

    // Render appropriate step
    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <div className="text-center">
                        <h2 className="text-xl font-bold mb-4">
                            {isAccessoryOnlyFlow ? 'Seleziona i Tappi' : `Seleziona i Tappi per NINFA ${normalizedProfileType}`}
                        </h2>
                        <p className="text-gray-600 mb-6">
                            {isAccessoryOnlyFlow ? 'Scegli i tappi per qualsiasi profilo NINFA' :
                                (profileConfig?.description || '')}
                        </p>

                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                            {availableCaps.map((capData) => {
                                const code = capData.code;
                                const profile = capData.profile;
                                const isSelected = selectedCaps.some(cap => cap.type === code);
                                const capName = code.includes('DX') ? 'Tappo Destro' :
                                    code.includes('SX') ? 'Tappo Sinistro' : 'Tappo Standard';

                                return (
                                    <button
                                        key={`${profile}-${code}`}
                                        onClick={() => handleCapSelection(capData)}
                                        className={`p-4 border-2 rounded-xl transition-all ${isSelected ? 'bg-red-100 border-red-500' : 'border-gray-200 bg-white hover:border-red-500 hover:shadow-lg'}`}
                                    >
                                        <div className="flex flex-col items-center">
                                            {capData.image ? (
                                                <div className="w-full h-20 mb-2 flex items-center justify-center">
                                                    <img
                                                        src={capData.image}
                                                        alt={capName}
                                                        className="h-16 object-contain"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextElementSibling.style.display = 'flex';
                                                        }}
                                                    />
                                                    <div className="w-16 h-16 hidden items-center justify-center bg-red-50 rounded-full">
                                                        <span className="text-2xl font-bold">
                                                            {code.includes('DX') ? '→' :
                                                                code.includes('SX') ? '←' : '◼'}
                                                        </span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="w-16 h-16 flex items-center justify-center mb-2 bg-red-50 rounded-full">
                                                    <span className="text-2xl font-bold">
                                                        {code.includes('DX') ? '→' :
                                                            code.includes('SX') ? '←' : '◼'}
                                                    </span>
                                                </div>
                                            )}
                                            <span className="font-medium text-gray-700">{capName}</span>
                                            <span className="text-sm text-gray-500">{code.replace('*', 'X')}</span>
                                            {isAccessoryOnlyFlow && (
                                                <span className="text-xs text-red-600 mt-1">NINFA {profile}</span>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="mt-6">
                            <button
                                onClick={() => selectedCaps.length > 0 && setStep(2)}
                                className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                                disabled={selectedCaps.length === 0}
                            >
                                Continua ({selectedCaps.length} selezionati)
                            </button>
                        </div>
                    </div>
                );
            case 2:
                return (
                    <div className="text-center">
                        <h2 className="text-xl font-bold mb-4">Configura Quantità e Colori</h2>
                        <p className="text-gray-600 mb-6">Per ogni tappo selezionato, scegli la quantità e il colore</p>

                        <div className="max-w-4xl mx-auto space-y-6 mb-6">
                            {selectedCaps.map((cap) => (
                                <div key={cap.type} className="p-6 border rounded-lg border-gray-200 bg-white">
                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
                                        {/* Tappo Info */}
                                        <div className="text-left">
                                            <h3 className="font-medium text-lg mb-2">
                                                {cap.type.includes('DX') ? 'Tappo Destro' :
                                                    cap.type.includes('SX') ? 'Tappo Sinistro' : 'Tappo Standard'}
                                                {isAccessoryOnlyFlow && cap.profile && (
                                                    <span className="text-red-600 ml-2">(NINFA {cap.profile})</span>
                                                )}
                                            </h3>
                                            <p className="text-sm text-gray-500">
                                                Codice: {cap.type.replace('*', cap.finish)}
                                            </p>
                                            <div className="flex items-center space-x-2 mt-3">
                                                <label className="text-sm font-medium">Quantità:</label>
                                                <input
                                                    type="number"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    min="1"
                                                    value={cap.quantity ?? ''}
                                                    onChange={(e) => {
                                                        const raw = e.target.value;
                                                        updateCapQuantity(cap.type, raw === '' ? '' : Math.max(1, parseInt(raw) || 1));
                                                    }}
                                                    className="w-24 p-3 text-base border rounded-md text-center"
                                                />
                                            </div>
                                        </div>

                                        {/* Colore Selection */}
                                        <div className="lg:col-span-2">
                                            <label className="block text-sm font-medium mb-3">Seleziona Colore:</label>
                                            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                                                {FINISHES.map((finishOption) => (
                                                    <button
                                                        key={finishOption.code}
                                                        onClick={() => updateCapFinish(cap.type, finishOption.code)}
                                                        className={`p-3 border rounded-lg transition-all ${cap.finish === finishOption.code
                                                            ? 'bg-red-100 border-red-500 ring-2 ring-red-300'
                                                            : 'border-gray-300 hover:border-gray-400'
                                                            }`}
                                                    >
                                                        <div className="flex flex-col items-center">
                                                            <div
                                                                className="w-8 h-8 rounded-full mb-1 border border-gray-300"
                                                                style={{ backgroundColor: finishOption.color }}
                                                            ></div>
                                                            <span className="text-xs font-medium">{finishOption.code}</span>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                            <p className="text-xs text-gray-500 mt-2">
                                                Selezionato: {FINISHES.find(f => f.code === cap.finish)?.name || cap.finish}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg mb-6">
                            <h3 className="font-medium mb-2">Riepilogo Prodotti</h3>
                            {generateProductCodes().map((product, index) => (
                                <div key={index} className="text-sm py-1">
                                    <span className="font-medium">{product.code}</span> -
                                    Qty: {product.quantity} - {product.description}
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-center space-x-4 mt-6">
                            <button
                                onClick={() => setStep(1)}
                                className="px-6 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                            >
                                Indietro
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={selectedCaps.some(c => !c.quantity || c.quantity === '' || parseInt(c.quantity) < 1)}
                                className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Aggiungi al Carrello
                            </button>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="space-y-6">
            {renderStep()}
            <RALModal
                isOpen={showRALModal}
                onClose={handleRALClose}
                onConfirm={handleRALConfirm}
                finishType={pendingFinishCode}
            />
        </div>
    );
};

export default CapSelector;